import { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import { useAuth } from "@/context/AuthContext";
import { hasPerm, isStaffAdmin } from "@/lib/perms";
import { AHeader, ACard, ATable, inputDark } from "@/components/shared/AdminKit";
import { QualityBadge } from "@/components/shared/Badges";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Link2, Unlink, Wand2, ChevronLeft, ChevronRight } from "lucide-react";

const REVIEW = {
  approved: "bg-emerald-500/20 text-emerald-400",
  pending: "bg-amber-500/20 text-amber-400",
  rejected: "bg-red-500/20 text-red-400",
};

export default function AdminMapping() {
  const { t } = useContent();
  const { user } = useAuth();
  const isAdm = isStaffAdmin(user);
  const canMap = hasPerm(user, "MANAGE_PROVIDER_MAPPINGS");
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [facets, setFacets] = useState({ platforms: [], categories: [] });
  const [mapTarget, setMapTarget] = useState(null);
  const [f, setF] = useState({ platform: "", category: "", quality: "", mapped: "", search: "", page: 1 });
  const limit = 25;

  const load = () => {
    setLoading(true);
    const p = new URLSearchParams({ page: f.page, limit });
    if (f.platform) p.set("platform", f.platform);
    if (f.category) p.set("category", f.category);
    if (f.quality) p.set("quality", f.quality);
    if (f.mapped) p.set("mapped", f.mapped);
    if (f.search) p.set("search", f.search);
    return api.get(`/admin/provider-services?${p}`).then((r) => { setData(r.data); setLoading(false); });
  };
  useEffect(() => { load(); }, [f.platform, f.category, f.quality, f.mapped, f.page]); // eslint-disable-line
  useEffect(() => {
    api.get("/admin/provider-services/facets").then((r) => setFacets(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v, page: 1 }));
  const normalize = async () => {
    const { data } = await api.post("/admin/provider-services/normalize");
    toast.success(`${data.normalized} ${t("map.normalized", "service(s) fournisseur normalisé(s)")}`);
    api.get("/admin/provider-services/facets").then((r) => setFacets(r.data)).catch(() => {});
    load();
  };
  const unmap = async (id) => { await api.post(`/admin/provider-services/${id}/unmap`); toast.success(t("map.unmapped", "Démappé — repassé en attente")); load(); };

  const pages = Math.max(1, Math.ceil(data.total / limit));

  return (
    <div>
      <AHeader title={t("map.title", "Mapping des services")} subtitle={t("map.subtitle", "Normalisation → Mapping → Routeur. Rattachez les services fournisseurs à vos paliers Economy / Standard / Premium.")}
        action={isAdm ? <button onClick={normalize} data-testid="mapping-normalize-btn" className="inline-flex items-center gap-2 rounded-full bg-accent px-5 py-2.5 text-sm font-medium text-white hover:opacity-90"><Wand2 className="h-4 w-4" /> {t("map.normalize", "Normaliser")}</button> : null} />

      <ACard className="mb-4">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
          <FSel label={t("map.f.platform", "Plateforme")} value={f.platform} onChange={(v) => set("platform", v)} options={facets.platforms} testid="filter-platform" t={t} />
          <FSel label={t("map.f.category", "Catégorie")} value={f.category} onChange={(v) => set("category", v)} options={facets.categories} testid="filter-category" t={t} />
          <FSel label={t("map.f.quality", "Qualité")} value={f.quality} onChange={(v) => set("quality", v)} options={["economy", "standard", "premium"]} testid="filter-quality" t={t} />
          <FSel label={t("map.f.mapped", "Mappé")} value={f.mapped} onChange={(v) => set("mapped", v)} options={[["true", t("map.mapped_yes", "Mappés")], ["false", t("map.mapped_no", "Non mappés")]]} testid="filter-mapped" t={t} />
          <div className="col-span-2">
            <label className="text-xs text-slate-400">{t("map.search", "Recherche")}</label>
            <div className="flex gap-2">
              <input value={f.search} onChange={(e) => setF((s) => ({ ...s, search: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && set("search", f.search)} data-testid="mapping-search" placeholder={t("map.search_ph", "nom du service…")} className={inputDark} />
            </div>
          </div>
        </div>
      </ACard>

      <ACard>
        <div className="mb-3 flex items-center justify-between text-sm text-slate-400">
          <span data-testid="mapping-total">{data.total.toLocaleString()} {t("map.count", "service(s) fournisseur")}</span>
          <div className="flex items-center gap-3">
            <button disabled={f.page <= 1} onClick={() => setF((s) => ({ ...s, page: s.page - 1 }))} data-testid="mapping-prev" className="disabled:opacity-30"><ChevronLeft className="h-4 w-4" /></button>
            <span>{t("map.page", "Page")} {f.page} / {pages}</span>
            <button disabled={f.page >= pages} onClick={() => setF((s) => ({ ...s, page: s.page + 1 }))} data-testid="mapping-next" className="disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
        {loading ? <div className="py-8 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-accent" /></div>
          : data.items.length === 0 ? <div className="py-10 text-center text-slate-400">{t("map.empty", "Aucun service. Cliquez « Normaliser » après une synchronisation fournisseur.")}</div>
            : (
              <ATable head={[t("map.col.service", "Service fournisseur"), t("map.f.platform", "Plateforme"), t("map.f.category", "Catégorie"), t("map.col.suggested", "Qualité suggérée"), t("map.col.cost", "Coût/1k"), t("map.col.minmax", "Min-Max"), t("map.col.review", "Révision"), t("map.col.mapped_to", "Mappé à"), t("map.col.action", "Action")]}>
                {data.items.map((s) => (
                  <tr key={s.id} className="border-b border-slate-800/60 last:border-0" data-testid={`ps-row-${s.id}`}>
                    <td className="px-3 py-3 max-w-[240px] truncate font-medium" title={s.name}>{s.name}</td>
                    <td className="px-3 py-3 text-slate-400">{s.norm_platform || "—"}</td>
                    <td className="px-3 py-3 text-slate-400">{s.norm_category || "—"}</td>
                    <td className="px-3 py-3">{s.suggested_quality ? <QualityBadge level={s.suggested_quality} /> : "—"}</td>
                    <td className="px-3 py-3">{money(s.provider_cost)}</td>
                    <td className="px-3 py-3 text-slate-400">{(s.min ?? "?")}-{(s.max ?? "?")}</td>
                    <td className="px-3 py-3"><span className={`rounded px-2 py-0.5 text-[10px] font-semibold ${REVIEW[s.review_status] || "bg-slate-700 text-slate-300"}`}>{s.review_status}</span></td>
                    <td className="px-3 py-3 max-w-[160px] truncate text-xs text-slate-400">{s.mapped_service_name || "—"}</td>
                    <td className="px-3 py-3">
                      {!canMap ? <span className="text-xs text-slate-500" data-testid={`map-readonly-${s.id}`}>—</span> : s.mapped_service_id
                        ? <button onClick={() => unmap(s.id)} data-testid={`unmap-ps-${s.id}`} className="inline-flex items-center gap-1 text-red-400" title={t("map.unmap", "Démapper")}><Unlink className="h-4 w-4" /></button>
                        : <button onClick={() => setMapTarget(s)} data-testid={`map-ps-${s.id}`} className="inline-flex items-center gap-1 text-accent" title={t("map.map", "Mapper")}><Link2 className="h-4 w-4" /></button>}
                    </td>
                  </tr>
                ))}
              </ATable>
            )}
      </ACard>
      {mapTarget && <MapDialog ps={mapTarget} onClose={() => setMapTarget(null)} onDone={load} t={t} />}
    </div>
  );
}

function MapDialog({ ps, onClose, onDone, t }) {
  const [serviceId, setServiceId] = useState("");
  const [quality, setQuality] = useState(ps.suggested_quality || "standard");
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState(ps.norm_platform || "");
  const [debouncedQ, setDebouncedQ] = useState(ps.norm_platform || "");
  const [opts, setOpts] = useState([]);
  const [searching, setSearching] = useState(false);

  // Server-side searchable picker (no full-catalogue load): /admin/services?search=&page_size=50.
  useEffect(() => { const h = setTimeout(() => setDebouncedQ(q), 350); return () => clearTimeout(h); }, [q]);
  useEffect(() => {
    setSearching(true);
    const p = new URLSearchParams({ page: 1, page_size: 50 });
    if (debouncedQ.trim()) p.set("search", debouncedQ.trim());
    api.get(`/admin/services?${p}`).then((r) => setOpts(r.data.items || [])).catch(() => setOpts([])).finally(() => setSearching(false));
  }, [debouncedQ]);

  const save = async () => {
    if (!serviceId) return;
    setLoading(true);
    try {
      await api.post(`/admin/provider-services/${ps.id}/map`, { service_id: serviceId, quality });
      toast.success(t("map.mapped_ok", "Service mappé et approuvé")); onClose(); onDone();
    } catch { toast.error(t("map.map_error", "Échec du mapping")); } finally { setLoading(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 text-white border-slate-800" data-testid="map-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{t("map.dlg.title", "Mapper vers un service client")}</DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">« {ps.name} » — {t("map.dlg.suggestion", "suggestion auto:")} {ps.suggested_quality || "standard"}. {t("map.dlg.hint", "Vous pouvez corriger le palier.")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <label className="text-xs text-slate-400">{t("map.dlg.service", "Service client (palier)")}</label>
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="map-service-search" placeholder={t("map.dlg.search_ph", "Rechercher un service client…")} className={inputDark + " mb-2"} />
            <Select value={serviceId || undefined} onValueChange={setServiceId}>
              <SelectTrigger data-testid="map-service-select" className="border-slate-700 bg-slate-800 text-white"><SelectValue placeholder={searching ? t("map.dlg.searching", "Recherche…") : t("map.dlg.choose", "Choisir un service…")} /></SelectTrigger>
              <SelectContent className="max-h-72">{opts.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-400">{t("map.dlg.tier", "Palier (ajustable)")}</label>
            <Select value={quality} onValueChange={setQuality}>
              <SelectTrigger data-testid="map-quality-select" className="border-slate-700 bg-slate-800 text-white"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="economy">Economy</SelectItem><SelectItem value="standard">Standard</SelectItem><SelectItem value="premium">Premium</SelectItem></SelectContent>
            </Select>
          </div>
          <button onClick={save} disabled={loading || !serviceId} data-testid="map-save-btn" className="flex w-full items-center justify-center gap-2 rounded-full bg-accent py-2.5 text-sm font-medium text-white disabled:opacity-50">{loading && <Loader2 className="h-4 w-4 animate-spin" />} {t("map.dlg.save", "Mapper & approuver")}</button>
          <p className="text-xs text-slate-500">{t("map.dlg.note", "Le mapping approuve ce service et l'ajoute au pool de candidats du routeur.")}</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FSel({ label, value, onChange, options, testid, t }) {
  const opts = options.map((o) => (Array.isArray(o) ? o : [o, o]));
  return (
    <div>
      <label className="text-xs text-slate-400">{label}</label>
      <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
        <SelectTrigger data-testid={testid} className="border-slate-700 bg-slate-800 text-white"><SelectValue placeholder={t("map.all", "Tous")} /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t("map.all", "Tous")}</SelectItem>
          {opts.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
